import { useEffect, useState } from 'react';

import { clearTokenFromUrl, readTokenFromUrl } from './token-from-url';

/**
 * The one-time token of an emailed link, as a React value.
 *
 * It is read when the page opens AND whenever the address changes WITHOUT a reload: pasting a new link into the
 * address bar of a tab that is already on this same page changes only the `#fragment`, which the browser treats as an
 * in-page navigation — no reload, so a value read once would stay stale (« lien incomplet » for a perfectly good link).
 *
 * Every time a token is read it is removed from the address bar at once, so it stays in neither the history nor a
 * screenshot. `replaceState` does not fire `hashchange`, so clearing it cannot loop.
 */
export function useUrlToken(): string | null {
  const [token, setToken] = useState(() =>
    readTokenFromUrl(window.location.hash, window.location.search),
  );

  useEffect(() => {
    clearTokenFromUrl();

    function onHashChange(): void {
      const next = readTokenFromUrl(window.location.hash, window.location.search);
      if (next !== null) {
        setToken(next);
        clearTokenFromUrl();
      }
    }
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  return token;
}
